import { useEffect, useState, useCallback } from "react";
import { Copy, Check, Gift, Users, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { getStoredReferralCode, clearStoredReferralCode } from "@/pages/ReferralCapturePage";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "—";
  }
}

/**
 * Compact referral widget — invite link + counters + extended-until banner.
 *
 * Render it on inbox / settings. Self-contained: fetches `/api/referral/me`,
 * auto-attaches a stored referral code if one exists (cookie / localStorage),
 * and copies the link to clipboard.
 */
export default function ReferralWidget({ className = "", onLoaded }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const r = await api.get("/referral/me");
      setData(r.data);
      if (onLoaded) onLoaded(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [onLoaded]);

  // On mount: try auto-capture using any stored code, then load summary.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = getStoredReferralCode();
      if (stored) {
        try {
          const r = await api.post("/referral/capture", { code: stored });
          if (r?.data?.ok) clearStoredReferralCode();
        } catch {
          /* ignore — user's not eligible (e.g. already attached) */
        }
      }
      if (!cancelled) await reload();
    })();
    return () => { cancelled = true; };
  }, [reload]);

  const copy = async () => {
    if (!data?.referral_link) return;
    try {
      await navigator.clipboard.writeText(data.referral_link);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy link");
    }
  };

  if (loading || !data) return null;

  const showExtended =
    data.reward_days_total > 0
    && data.effective_expires_at
    && data.base_expires_at
    && data.effective_expires_at !== data.base_expires_at;

  return (
    <div
      className={`bg-white border border-[#E5E5E5] rounded-xl p-4 space-y-3 ${className}`}
      data-testid="referral-widget"
    >
      <div className="flex items-center gap-2">
        <Gift size={16} className="text-[var(--color-l-accent,#CC0000)]" />
        <h3 className="text-sm font-semibold text-[#111111]">Invite friends</h3>
      </div>

      <p className="text-xs text-[#555555]">
        Share your link. Each friend who activates their inbox earns you both
        +1 day on your plan.
      </p>

      <div className="flex items-center gap-2">
        <code
          className="flex-1 text-xs font-mono bg-[#F5F5F5] border border-[#E5E5E5] rounded-md px-2.5 py-1.5 truncate text-[#333333]"
          data-testid="referral-link"
          title={data.referral_link}
        >
          {data.referral_link}
        </code>
        <Button
          size="sm"
          variant="outline"
          className="h-8 gap-1 text-xs"
          onClick={copy}
          disabled={!data.referral_link}
          data-testid="referral-copy-btn"
        >
          {copied ? <Check size={13} /> : <Copy size={13} />} Copy
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2 pt-1">
        <Stat icon={Users} label="Invited" value={data.total_invites} testId="referral-stat-invited" />
        <Stat icon={Check} label="Activated" value={data.activated_invites} testId="referral-stat-activated" />
        <Stat icon={Gift} label="Days earned" value={data.reward_days_total} testId="referral-stat-days" />
      </div>

      <div className="pt-2 border-t border-[#F0F0F0] text-xs text-[#555555] flex items-start gap-1.5" data-testid="referral-expiry">
        <Calendar size={12} className="mt-0.5 shrink-0" />
        <div className="space-y-0.5 leading-snug">
          <div>
            Plan active until: <span className="font-mono text-[#111111]">{fmtDate(data.base_expires_at)}</span>
          </div>
          {showExtended ? (
            <>
              <div>
                Extended to: <span className="font-mono text-[#111111]">{fmtDate(data.effective_expires_at)}</span>
              </div>
              <div className="text-[var(--color-l-accent,#CC0000)] font-medium" data-testid="referral-expiry-bonus">
                +{data.reward_days_total} day{data.reward_days_total !== 1 ? "s" : ""} from referrals
              </div>
            </>
          ) : (
            <div className="text-[#888888] italic" data-testid="referral-expiry-empty">
              Invite friends and earn extra days.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div
      className="bg-[#FAFAFA] border border-[#F0F0F0] rounded-lg px-2 py-2 text-center"
      data-testid={testId}
    >
      <Icon size={12} className="mx-auto text-[#888888] mb-0.5" />
      <div className="text-base font-semibold text-[#111111]" data-testid={`${testId}-value`}>
        {value ?? 0}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-[#888888]">{label}</div>
    </div>
  );
}
